import { useState } from 'react';
import { Modal, Button, TextField, BlockStack, Text, Banner, InlineStack } from '@shopify/polaris';
import { MagicIcon } from '@shopify/polaris-icons';

/*
  "Write with AI" next to the PDF introduction: drafts (or rewrites) the introduction from the quotation's scope, the
  customer and an optional instruction. The draft only lands in the editor; saving stays the user's own action.
*/

const LANGUAGE_NAMES = { E: 'English', N: 'Dutch', F: 'French' };

const QUICK_INSTRUCTIONS = ['Budget / indicative offer', 'Follow-up after our site visit', 'Mention the lead time starts at the advance payment', 'Short and formal', 'Improve my current text'];

export default function IntroductionWriter({ quotationId, language, currentText, onUse, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasCurrent = Boolean(
    String(currentText || '')
      .replace(/<[^>]*>|\[PAGEBREAK\]/g, '')
      .trim()
  );

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/introduction-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions, language, current: currentText || '' })
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.message || `Request failed (${response.status})`);
      setDraft(body.html);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addQuickInstruction = (text) => setInstructions((prev) => (prev.trim() ? `${prev.trim().replace(/[.;,]?$/, '.')} ${text}` : text));

  const close = () => {
    setOpen(false);
    setDraft(null);
    setError(null);
  };

  return (
    <>
      <Button icon={MagicIcon} variant="plain" size="slim" onClick={() => setOpen(true)} disabled={disabled}>
        {hasCurrent ? 'Rewrite with AI' : 'Write with AI'}
      </Button>
      <Modal
        open={open}
        onClose={close}
        title={hasCurrent ? 'Rewrite the introduction' : 'Write the introduction'}
        primaryAction={
          draft
            ? {
                content: hasCurrent ? 'Replace current text' : 'Use this text',
                onAction: () => {
                  onUse(draft);
                  close();
                }
              }
            : { content: 'Write', onAction: generate, loading }
        }
        secondaryActions={draft ? [{ content: loading ? 'Writing…' : 'Try again', onAction: generate, disabled: loading }] : [{ content: 'Cancel', onAction: close }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <TextField
              label="What should it say? (optional)"
              value={instructions}
              onChange={setInstructions}
              multiline={3}
              autoComplete="off"
              placeholder="E.g. contact is Mr. Janssens, thank him for the visit last week, stress the energy savings of the afterburner…"
              helpText={`Written in ${LANGUAGE_NAMES[language] || language} from the quoted lines, the customer and ${
                hasCurrent ? 'your current text' : 'earlier introductions'
              }. Nothing is saved until you save the terms.`}
            />
            <InlineStack gap="150" wrap>
              {QUICK_INSTRUCTIONS.filter((text) => hasCurrent || text !== 'Improve my current text').map((text) => (
                <button
                  key={text}
                  type="button"
                  onClick={() => addQuickInstruction(text)}
                  style={{ border: '1px solid #d0d5dd', background: '#fff', borderRadius: '999px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', color: '#344054' }}
                >
                  {`+ ${text}`}
                </button>
              ))}
            </InlineStack>
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}
            {draft && (
              <BlockStack gap="100">
                <Text as="p" variant="headingSm">
                  Draft
                </Text>
                <div
                  style={{
                    border: '1px solid #bdb4fe',
                    background: loading ? '#f9fafb' : '#fcfaff',
                    opacity: loading ? 0.6 : 1,
                    borderRadius: '8px',
                    padding: '10px 14px',
                    maxHeight: '360px',
                    overflow: 'auto',
                    fontSize: '13.5px',
                    lineHeight: 1.55
                  }}
                  // server-built HTML: escaped model text in <p>/<strong> only
                  dangerouslySetInnerHTML={{ __html: draft.replace(/<p><\/p>/g, '<p>&nbsp;</p>') }}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  Not quite right? Adjust the instruction above and click Try again.
                </Text>
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
